/**
 * WorksheetPrintPreview — Story 58-8
 * Modal with PDF preview (iframe), print/download/email buttons.
 */

import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

interface WorksheetPrintPreviewProps {
  open: boolean;
  onClose: () => void;
  worksheetId: string;
  worksheetNumber: string;
  partnerEmail?: string;
}

export function WorksheetPrintPreview({
  open,
  onClose,
  worksheetId,
  worksheetNumber,
  partnerEmail,
}: WorksheetPrintPreviewProps) {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState(partnerEmail ?? '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<string | null>(null);

  const loadPdf = useCallback(async () => {
    setIsLoading(true);
    try {
      const { accessToken } = useAuthStore.getState();
      const response = await fetch(`/api/v1/worksheets/${worksheetId}/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ includeJuridical: true }),
      });

      if (!response.ok) {
        throw new Error(t('worksheetPrint.pdfGenerationFailed'));
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('PDF load error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    if (open) {
      loadPdf();
      setEmailAddress(partnerEmail ?? '');
      setEmailResult(null);
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, worksheetId]);

  const handlePrint = () => {
    if (pdfUrl) {
      const iframe = document.querySelector<HTMLIFrameElement>('#worksheet-pdf-preview');
      if (iframe?.contentWindow) {
        iframe.contentWindow.print();
      }
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `${worksheetNumber}.pdf`;
      a.click();
    }
  };

  const handleEmail = async () => {
    if (!emailAddress) return;
    setEmailSending(true);
    setEmailResult(null);

    try {
      const result = await api.post<{ data: { success: boolean; sentTo: string } }>(
        `/worksheets/${worksheetId}/email`,
        { email: emailAddress }
      );
      if (result.data?.success) {
        setEmailResult(`${t('worksheetPrint.emailSent')}: ${result.data.sentTo}`);
      } else {
        setEmailResult(t('worksheetPrint.unknownError'));
      }
    } catch {
      setEmailResult(t('worksheetPrint.emailSendFailed'));
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('worksheetPrint.title')} — {worksheetNumber}
          </DialogTitle>
        </DialogHeader>

        {/* PDF Preview */}
        <div className="flex-1 min-h-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">{t('worksheetPrint.generating')}</span>
            </div>
          )}
          {pdfUrl && !isLoading && (
            <iframe
              id="worksheet-pdf-preview"
              src={pdfUrl}
              className="w-full h-full"
              title={t('worksheetPrint.pdfTitle')}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-3 border-t">
          <div className="flex gap-2">
            <Button onClick={handlePrint} disabled={!pdfUrl || isLoading}>
              {t('worksheetPrint.print')}
            </Button>
            <Button variant="outline" onClick={handleDownload} disabled={!pdfUrl || isLoading}>
              {t('worksheetPrint.downloadPdf')}
            </Button>
          </div>

          {/* Email */}
          <div className="flex gap-2 items-center">
            <Input
              type="email"
              placeholder={t('worksheetPrint.emailPlaceholder')}
              value={emailAddress}
              onChange={e => setEmailAddress(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleEmail}
              disabled={!emailAddress || emailSending}
            >
              {emailSending ? t('worksheetPrint.sending') : t('worksheetPrint.sendEmail')}
            </Button>
          </div>
          {emailResult && (
            <p
              className={`text-sm ${emailResult.startsWith(t('worksheetPrint.errorPrefix')) ? 'text-red-500' : 'text-green-600'}`}
            >
              {emailResult}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
